import { RefObject, useCallback, useEffect, useRef } from 'react';
import useRefInView from '~/hooks/common/ui/useRefInView';
import { generateErrorLogger } from '~/utilities/loggers';

const logVideoError = generateErrorLogger('WhyBuyWithImageryCard');

const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

type UseWhyBuyCardVideoOptions = {
  cardRef: RefObject<HTMLElement | null>;
  isActive: boolean;
  isMobile: boolean;
  onActivate: () => void;
  onMediaError?: () => void;
  src?: string;
};

export default function useWhyBuyCardVideo({
  cardRef,
  isActive,
  isMobile,
  onActivate,
  onMediaError,
  src
}: UseWhyBuyCardVideoOptions) {
  const previousIsMobileRef = useRef(isMobile);
  const videoRef = useRef<HTMLVideoElement>(null);
  const isInView = useRefInView(cardRef, { threshold: 0.5 });

  const play = useCallback(
    (video: HTMLVideoElement) => {
      if (prefersReducedMotion()) return;

      video.play()?.catch((err: unknown) => {
        if (err instanceof Error && err.name === 'AbortError') return;
        logVideoError(err, { src });
      });
    },
    [src]
  );

  const reset = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    video.pause();
    video.currentTime = 0;
  }, []);

  useEffect(() => {
    const didIsMobileChange = previousIsMobileRef.current !== isMobile;
    previousIsMobileRef.current = isMobile;

    if (didIsMobileChange) reset();
    else if (!isMobile && !isActive) videoRef.current?.pause();
  }, [isActive, isMobile, reset]);

  useEffect(() => {
    if (!isMobile) return;

    const video = videoRef.current;
    if (!video) return;

    if (!isInView) {
      reset();

      return;
    }

    video.currentTime = 0;
    video.loop = false;
    play(video);
  }, [isInView, isMobile, play, reset]);

  useEffect(() => {
    if (videoRef.current?.error) onMediaError?.();
  }, [onMediaError, src]);

  const onMouseEnter = useCallback(() => {
    const video = videoRef.current;
    if (!video || isMobile || prefersReducedMotion()) return;

    onActivate();
    video.loop = true;
    play(video);
  }, [isMobile, onActivate, play]);

  const onMouseLeave = useCallback(() => {
    const video = videoRef.current;
    if (video) video.loop = false;
  }, []);

  const onError = () => {
    logVideoError(
      videoRef.current?.error ?? new Error('Video failed to load'),
      { src }
    );
    onMediaError?.();
  };

  return { onError, onMouseEnter, onMouseLeave, videoRef };
}
